import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from 'axios';
import './Tier.css';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const Tier = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryTierId = searchParams.get("Tierid");
  const { Tierid: stateTierId } = location.state || {};
  const Tierid = stateTierId || queryTierId;
  const navigate = useNavigate();
  const [tier, setTier] = useState({ id: Date.now(), name: '', academies: [] });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [allAcademies, setAllAcademies] = useState([]);
  const [sportOptions, setSportOptions] = useState([]);

  const [singleSportRows, setSingleSportRows] = useState([
    { id: Date.now(), sport: null, elite: '', premium: '', standard: '' }
  ]);
  const [multiSportRows, setMultiSportRows] = useState([
    { id: Date.now() + 1, elite: '', premium: '', standard: '' }
  ]);

  const oldPayloadRef = useRef(null);

  const handleChange = (field, value) => {
    setTier({ ...tier, [field]: value });
  };

  const getFilteredOptions = () => academyOptions;

  const normalizeTier = (obj = {}) => {
    const academyIds = Array.isArray(obj.academyIds) ? [...obj.academyIds].map(Number).sort((a, b) => a - b) : [];
    const normFactors = Array.isArray(obj.tierFactorForms) ? obj.tierFactorForms.map(f => ({
      id: f.id ?? null,
      sportId: f.sportId == null ? null : Number(f.sportId),
      type: String(f.type || '').toUpperCase() === 'MULTI' ? 'MULTI' : 'SINGLE',
      elite: Number(f.elite ?? 0),
      premium: Number(f.premium ?? 0),
      standard: Number(f.standard ?? 0),
    })) : [];

    normFactors.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'SINGLE' ? -1 : 1;
      const as = a.sportId ?? Number.MAX_SAFE_INTEGER;
      const bs = b.sportId ?? Number.MAX_SAFE_INTEGER;
      return as - bs;
    });

    return {
      id: obj.id ?? null,
      name: (obj.name || '').trim(),
      academyIds,
      tierFactorForms: normFactors
    };
  };

  const buildSubmitPayload = () => {
    const old = oldPayloadRef.current || normalizeTier({});
    const keyOf = (f) => `${f.type}|${f.sportId == null ? 'null' : Number(f.sportId)}`;
    const oldMap = new Map((old.tierFactorForms || []).map(f => [keyOf(f), f.id ?? null]));

    const singleSportData = singleSportRows.filter(row => row.sport && row.sport.value).map(row => {
      const draft = {
        sportId: Number(row.sport.value),
        type: 'SINGLE',
        elite: Number(row.elite || 0),
        premium: Number(row.premium || 0),
        standard: Number(row.standard || 0),
      };
      const id = oldMap.get(keyOf(draft)) ?? null;
      return { id, ...draft };
    });

    const multiSportData = multiSportRows.map(row => {
      const draft = {
        sportId: 0, 
        type: 'MULTI',
        elite: Number(row.elite || 0),
        premium: Number(row.premium || 0),
        standard: Number(row.standard || 0),
      };
      const id = oldMap.get(keyOf(draft)) ?? null;
      return { id, ...draft };
    });

    const payload = {
      id: Tierid || null,
      name: (tier.name || '').trim(),
      academyIds: tier.academies.map(a => Number(a.value)),
      tierFactorForms: [...singleSportData, ...multiSportData]
    };

    return normalizeTier(payload);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_TIER_ACADEMY, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const data = response.data.data;

        const formattedAcademies = Object.entries(data.academies).map(([key, value]) => ({
          value: key,
          label: value
        }));
        setAllAcademies(formattedAcademies);

        const formattedSports = Object.entries(data.sports).map(([key, value]) => ({
          value: key,
          label: value
        }));
        setSportOptions(formattedSports);

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);


  useEffect(() => {
    const fetchTierDetails = async () => {

      if (!Tierid) {
        setAcademyOptions(allAcademies);
        oldPayloadRef.current = normalizeTier({ id: null, name: '', academyIds: [], tierFactorForms: [] });
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_TIER_EDIT}?tier_id=${Tierid}`, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const tierData = response.data?.data?.tierList?.[0];

        if (tierData) {
          const selectedAcademies = tierData.academies.map((academy) => ({
            value: academy.academyId.toString(),
            label: academy.academyName,
          }));

          const remainingOptions = allAcademies.filter(
            (a) => !selectedAcademies.some((sel) => sel.value === a.value)
          );

          const singleRows = (tierData.tierFactorForms || [])
            .filter(factor => String(factor.type).toUpperCase() === 'SINGLE')
            .map(factor => ({
              id: factor.id || Date.now(),
              sport: sportOptions.find(s => parseInt(s.value, 10) === Number(factor.sportId)) || null,
              elite: factor.elite,
              premium: factor.premium,
              standard: factor.standard,
            }));

          const multiRows = (tierData.tierFactorForms || [])
            .filter(factor => String(factor.type).toUpperCase() === 'MULTI')
            .map(factor => ({
              id: factor.id || Date.now() + 1,
              elite: factor.elite,
              premium: factor.premium,
              standard: factor.standard,
            }));

          setTier({
            id: tierData.tierId,
            name: tierData.tierName,
            academies: selectedAcademies,
          });

          setSelectedAcademy(selectedAcademies);
          setAcademyOptions(remainingOptions);
          setSingleSportRows(singleRows.length > 0 ? singleRows : [{ id: Date.now(), sport: null, elite: '', premium: '', standard: '' }]);
          setMultiSportRows(multiRows.length > 0 ? multiRows : [{ id: Date.now() + 1, elite: '', premium: '', standard: '' }]);


          oldPayloadRef.current = normalizeTier({
            id: tierData.tierId,
            name: tierData.tierName,
            academyIds: (tierData.academies || []).map(a => a.academyId),
            tierFactorForms: (tierData.tierFactorForms || []).map(f => ({
              id: f.id ?? null,
              sportId: f.sportId ?? null,
              type: f.type,
              elite: f.elite,
              premium: f.premium,
              standard: f.standard
            }))
          });
        }
      } catch (error) {
        console.error('Error fetching tier details:', error);
      }
    };

    fetchTierDetails();
  }, [Tierid, allAcademies, sportOptions]);

  const getAvailableSportOptions = (currentIndex) => {
    const selectedSports = singleSportRows
      .map((row) => row.sport?.value)
      .filter((val, i) => val && i !== currentIndex);

    return sportOptions.filter(
      (option) => !selectedSports.includes(option.value)
    );
  };

  const handleSingleSportChange = (index, field, value) => {
    const updatedRows = [...singleSportRows];
    updatedRows[index][field] = value;
    setSingleSportRows(updatedRows);
  };

  const handleMultiSportChange = (index, field, value) => {
    const updatedRows = [...multiSportRows];
    updatedRows[index][field] = value;
    setMultiSportRows(updatedRows);
  };

  const addSingleSportRow = () => {
    setSingleSportRows([...singleSportRows, { id: Date.now(), sport: null, elite: '', premium: '', standard: '' }]);
  };

  const removeSingleSportRow = (index) => {
    if (singleSportRows.length > 1) {
      const updatedRows = singleSportRows.filter((_, i) => i !== index);
      setSingleSportRows(updatedRows);
    }
  };

  const addMultiSportRow = () => {
    setMultiSportRows([...multiSportRows, { id: Date.now(), elite: '', premium: '', standard: '' }]);
  };

  const removeMultiSportRow = (index) => {
    if (multiSportRows.length > 1) {
      const updatedRows = multiSportRows.filter((_, i) => i !== index);
      setMultiSportRows(updatedRows);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tier.name.trim()) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "Please enter a Tier name.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    if (tier.academies.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "Please select at least one academy.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    setIsSubmitted(true);
    const payload = buildSubmitPayload();
    const isUpdate = !!Tierid;
    if (isUpdate) {
      const oldPayload = oldPayloadRef.current || normalizeTier({});
      const { digest, html } = buildDiffPreview(oldPayload, payload);
      if (!digest.length) {
        Swal.fire({
          icon: "info",
          title: "Nothing to update",
          text: "No differences detected.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2500,
        });
        setIsSubmitted(false);
        return;
      }
      const confirm = await Swal.fire({
        title: "Apply these changes?",
        html,
        showCancelButton: true,
        confirmButtonText: "Update",
      });
      if (!confirm.isConfirmed) {
        setIsSubmitted(false);
        return;
      }
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_TIER_CREATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok || responseData.success === false) {
        throw new Error(responseData.success_message || 'API call failed');
      }

      Swal.fire({
        icon: "success",
        title: "Submitted!",
        text: isUpdate ? "Tier updated successfully." : "Tier successfully created.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      const affected_id = String(Tierid || responseData?.data?.id || payload.name);
      await fireAudit({
        entity: 'tier',
        affected_id,
        action: isUpdate ? 'update' : 'create',
        section: 'Academy',
        subSection: 'Academy Tier',
        businessApi: process.env.REACT_APP_API_TIER_CREATION,
        businessMethod: 'POST',
        oldPayload: isUpdate ? (oldPayloadRef.current || normalizeTier({})) : {},
        newPayload: payload,
        extra: { page: 'Tier' }
      });

      oldPayloadRef.current = payload;

      navigate("/tierList");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.message || "Something went wrong. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setIsSubmitted(false);
    }
  };

  return (
    <form className="enrollment-container" onSubmit={handleSubmit}>
      <div className="breadcrumb">
        <Link to="/tierList" className="breadcrumb-link">Tier List</Link> &gt; New Tier
      </div>

      <div className="tier-cards-container">
        <div className="tier-card">
          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              id="tier-name"
              type="text"
              name="tier-name"
              className={`walkin-form ${tier.name ? "has-value" : ""}`}
              value={tier.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter Tier"
              required
            />
            {tier.name && <label htmlFor="tier-name">Tier Name</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <Select
              name="academies"
              isMulti
              options={getFilteredOptions()}
              value={tier.academies}
              onChange={(selected) => handleChange('academies', selected)}
              className={`walkin-form-dropdown ${tier.academies.length ? "has-value" : ""}`}
              classNamePrefix="walkin-select"
              placeholder="Select academies..."
              closeMenuOnSelect={false}
              isClearable
              styles={{
                control: (provided) => ({
                  ...provided,
                  padding: '5px',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
              }}
              menuPortalTarget={document.body}
            />
            {tier.academies.length > 0 && (
              <label htmlFor="academies">Academies</label>
            )}
          </div>
        </div>
      </div>
      {/* Single Sport Tier Table */}
      <div style={{ overflow: 'scroll' }}>
        <fieldset className="enrollment-form-fieldset enrollment-form-mt-3" style={{ marginTop: '0px' }}>
          <legend className="enrollment-form-legend">Single Sport</legend>
          <div className="rental-table-scroll">
            <table className="rental-entry-table">
              <thead>
                <tr>
                  <th>Sport</th>
                  <th>Elite</th>
                  <th>Premium</th>
                  <th>Standard</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {singleSportRows.map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ minWidth: '160px' }}>
                      <Select
                        options={getAvailableSportOptions(index)}
                        value={row.sport}
                        onChange={(selected) => handleSingleSportChange(index, 'sport', selected)}
                        placeholder="Select Sport"
                        className="tier-sport-select"
                        menuPortalTarget={document.body}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.elite === 0 ? '0' : row.elite || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleSingleSportChange(index, 'elite', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.premium === 0 ? '0' : row.premium || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleSingleSportChange(index, 'premium', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />
                    </td>


                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.standard === 0 ? '0' : row.standard || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleSingleSportChange(index, 'standard', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />

                    </td>

                    <td>
                      <button
                        type="button"
                        onClick={() => removeSingleSportRow(index)}
                        disabled={singleSportRows.length === 1}
                        className="enrollment-form-button enrollment-form-button-danger"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="plusButton" onClick={addSingleSportRow}>
            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
              <g mask="url(#mask0_21_345)">
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </g>
            </svg>
          </button>
        </fieldset>
      </div>

      {/* Multi Sport Tier Table */}
      <div style={{ overflow: 'scroll' }}>
        <fieldset className="enrollment-form-fieldset enrollment-form-mt-3" >
          <legend className="enrollment-form-legend">Multi Sport</legend>
          <div className="rental-table-scroll">
            <table className="rental-entry-table">
              <thead>
                <tr>
                  <th>Elite</th>
                  <th>Premium</th>
                  <th>Standard</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {multiSportRows.map((row, index) => (
                  <tr key={row.id}>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.elite === 0 ? '0' : row.elite || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleMultiSportChange(index, 'elite', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />

                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.premium === 0 ? '0' : row.premium || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleMultiSportChange(index, 'premium', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />

                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="rental-input"
                        placeholder="0"
                        value={row.standard === 0 ? '0' : row.standard || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const parsed = value === '' ? '' : parseFloat(value);
                            handleMultiSportChange(index, 'standard', parsed);
                          }
                        }}
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                      />

                    </td>
                    <td>
                      <button
                        type="button"
                        className="enrollment-form-button enrollment-form-button-danger"
                        onClick={() => removeMultiSportRow(index)}
                        disabled={multiSportRows.length === 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="plusButton" onClick={addMultiSportRow}>
            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
              <g mask="url(#mask0_21_345)">
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </g>
            </svg></button>
        </fieldset>
      </div>

      <div className="enrollment-form-section enrollment-form-mt-3">
        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitted}
        >
          {isSubmitted ? 'Submitted!' : (Tierid ? 'Update' : 'Submit')}
        </button>
      </div>
    </form >
  );
};

export default Tier;